/**
 * Query-string parameters whose values must never reach Datadog. The OIDC
 * callback (`/ui/auth/callback?token=…&refreshToken=…`) carries the live
 * Cassiopeia bearer JWT and refresh token in its query string. As
 * defense-in-depth we redact them in `beforeSend` (see datadog-init.ts) so
 * these credentials can never leave the browser via a recorded URL, whatever
 * the SDK's view-capture timing happens to be.
 *
 * Matched case-insensitively. Values are replaced with `REDACTED` so the URL
 * shape stays useful for analytics; the key itself is preserved.
 */
const SENSITIVE_PARAMS = new Set(['token', 'refreshtoken', 'refresh_token', 'access_token', 'id_token', 'code']);

const REDACTED = 'REDACTED';

/**
 * Returns `url` with the values of any {@link SENSITIVE_PARAMS} query
 * parameters replaced by `REDACTED`. Returns the input unchanged when it has
 * no query string. Falls back to regex redaction if the URL cannot be parsed,
 * so a malformed URL can never leak a token.
 */
export const scrubSensitiveParams = (url: string | undefined): string | undefined => {
  if (!url || !url.includes('?')) return url;

  try {
    const parsed = new URL(url, 'http://placeholder.invalid');
    const keysToRedact: string[] = [];
    parsed.searchParams.forEach((_value, key) => {
      if (SENSITIVE_PARAMS.has(key.toLowerCase())) keysToRedact.push(key);
    });
    if (keysToRedact.length === 0) return url;
    for (const key of keysToRedact) parsed.searchParams.set(key, REDACTED);
    // Reassemble without the placeholder origin if it was relative.
    return url.startsWith(parsed.origin) ? parsed.href : `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return redactWithRegex(url);
  }
};

const redactWithRegex = (url: string): string => {
  let result = url;
  for (const param of SENSITIVE_PARAMS) {
    result = result.replace(new RegExp(`([?&](?:${param})=)[^&#]*`, 'gi'), `$1${REDACTED}`);
  }
  return result;
};
